import { Link } from "react-router-dom";
import { Check, ArrowUpRight } from "lucide-react";
import { RevealLines, Reveal } from "@/components/Reveal";
import { useLanguage } from "@/context/LanguageContext";
import { IMAGES, PAGE_META } from "@/data/content";
import usePageMeta from "@/hooks/usePageMeta";

export default function Services() {
  const { t, lang } = useLanguage();
  usePageMeta({ ...PAGE_META[lang].services, lang, image: IMAGES.lifeScience });
  const imgs = [IMAGES.lifeScience, IMAGES.engineering];

  return (
    <div className="pt-32 pb-28 min-h-screen">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-6 lg:px-12">
        <span className="text-y5-yellow text-sm uppercase tracking-[0.3em]">{t.services.kicker}</span>
        <h1 className="font-display text-5xl sm:text-6xl md:text-8xl tracking-tight uppercase mt-4 mb-6 leading-[0.9]">
          <RevealLines lines={t.services.title} />
        </h1>
        <p className="text-y5-muted text-lg max-w-2xl mb-20">{t.services.subtitle}</p>

        <div className="space-y-4">
          {t.services.items.map((s, idx) => (
            <Reveal key={s.id} delay={idx * 0.05}>
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-0 border border-y5-line bg-y5-surface overflow-hidden">
                <div className="lg:col-span-5 relative min-h-[280px]">
                  <img src={imgs[idx]} alt={s.label} className="absolute inset-0 w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t lg:bg-gradient-to-r from-y5-surface via-y5-surface/40 to-transparent" />
                  <div className="absolute bottom-0 left-0 p-8">
                    <span className="text-y5-yellow text-xs uppercase tracking-widest">{s.tagline}</span>
                    <h2 className="font-display text-4xl md:text-5xl uppercase tracking-tight mt-2">{s.label}</h2>
                  </div>
                </div>
                <div className="lg:col-span-7 p-8 lg:p-12">
                  <p className="text-white/80 leading-relaxed mb-8">{s.desc}</p>
                  <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">
                    {s.list.map((it) => (
                      <li key={it} className="flex items-start gap-3 text-white/80 text-sm">
                        <Check size={16} className="text-y5-yellow mt-0.5 shrink-0" /> {it}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="border border-y5-line bg-y5-surface p-8">
            <h3 className="font-display text-2xl uppercase tracking-tight text-y5-yellow mb-3">{t.services.model.consulting.t}</h3>
            <p className="text-white/80 leading-relaxed">{t.services.model.consulting.d}</p>
          </div>
          <div className="border border-y5-line bg-y5-surface p-8">
            <h3 className="font-display text-2xl uppercase tracking-tight text-y5-yellow mb-3">{t.services.model.recruitment.t}</h3>
            <p className="text-white/80 leading-relaxed">{t.services.model.recruitment.d}</p>
          </div>
        </div>

        <div className="mt-16 flex flex-col md:flex-row items-center justify-between gap-6 border border-y5-line p-10 bg-y5-surface">
          <div>
            <h3 className="font-display text-3xl md:text-4xl uppercase tracking-tight">{t.services.needTitle}</h3>
            <p className="text-y5-muted mt-2">{t.services.needText}</p>
          </div>
          <Link to="/contact" data-testid="services-contact-cta" className="inline-flex items-center gap-2 bg-y5-yellow text-y5-ink px-8 py-4 font-semibold hover:scale-[1.03] transition-transform shrink-0">
            {t.services.contact} <ArrowUpRight size={18} />
          </Link>
        </div>
      </div>
    </div>
  );
}
